import { motion } from "framer-motion";
import { useState, useRef } from "react";
import heroImage from "@/assets/hero-grill.webp";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_VIDEO_URL: string | null = null;

const HeroSection = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useLanguage();

  return (
    <header role="banner" className="relative min-h-screen flex items-end pb-24 md:pb-32 overflow-hidden">
      <div className="absolute inset-0">
        {HERO_VIDEO_URL && !videoError && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlayThrough={() => setVideoLoaded(true)}
            onError={() => setVideoError(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              videoLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
        )}

        <div
          className={`absolute inset-0 transition-opacity duration-1000 ${
            videoLoaded && !videoError ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="absolute inset-0 animate-ken-burns">
            <img
              src={heroImage}
              alt={t("hero.image_alt")}
              className="w-full h-full object-cover"
              width={1920}
              height={2880}
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
      </div>

      <div className="relative z-10 px-6 max-w-5xl mx-auto w-full">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-5xl md:text-7xl lg:text-8xl leading-none tracking-tight mb-6 text-shadow-hero text-foreground"
        >
          FUEGOS JL
          <br />
          <span className="text-foreground/70">NORDIC</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-base md:text-lg text-foreground/80 max-w-lg mb-10 text-shadow-hero font-light"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.a
          href="#bestilling"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="inline-block border border-foreground/40 text-foreground font-display text-lg tracking-[0.2em] px-10 py-4 hover:bg-foreground/10 transition-all duration-300"
        >
          {t("hero.cta")}
        </motion.a>
      </div>
    </header>
  );
};

export default HeroSection;
